import {
  EventDispatcher,
  PortableEvent,
  WarningLevel,
  Warning,
  BaseVoucher,
} from '@sas-dvr/ltjs-commons';
import EventDetachHandle from '@sas-dvr/ltjs-commons/events/EventDetachHandle';
import { DownloadStatus, Report, TransportWarning } from '@sas-dvr/ltjs-transport';

import { ltjsProperty } from '../ltjs-decorators';

type VoucherCallback = (voucher: TransportVoucher) => void;

function _executeCallbacks(callbacks: VoucherCallback[], voucher: TransportVoucher) {
  for (const callback of callbacks) {
    callback(voucher);
  }
}

export class TransportVoucher {
  //
  // Voucher resolution callback lists
  //
  private _fulfilledCallbacks: VoucherCallback[] = [];
  private _cancelledCallbacks: VoucherCallback[] = [];
  private _warningCallbacks: VoucherCallback[] = [];
  private _alwaysCallbacks: VoucherCallback[] = [];

  //
  // The raw transport voucher
  //
  @ltjsProperty
  private _voucher: BaseVoucher | null = null;

  //
  // Event handles for the voucherEvent
  //
  private _voucherEventHandle: EventDetachHandle | null = null;
  private _downloadVoucherEventHandle: EventDetachHandle | null = null;
  //
  // Flags for cancelled and resolved state
  //
  private _cancelled = false;
  private _resolved = false;
  //
  // Warning used by forcing creation of
  // a warned TransportVoucher with createWarning()
  //
  private _warning: TransportWarning | null = null;
  //
  // The parent TransportVoucher, created by using then()
  // to create a voucher chain.
  //
  private _chainParent: TransportVoucher | null = null;
  //
  // timer id for all deferred resolution handlers
  //
  private _deferredResolutionTimerId = 0;

  constructor(voucher?: BaseVoucher);
  constructor(warningType: number, warningLevel: WarningLevel, messages: string);
  constructor(inference?: BaseVoucher | number, warningLevel?: WarningLevel, message?: string) {
    if (inference === undefined) {
      return;
    }
    if (inference instanceof BaseVoucher) {
      this.setVoucher(inference);
      return;
    }
    // FIXME why is exclamation point needed?
    this._warning = new Warning(inference, warningLevel!, message || '');
    this._scheduleDeferredResolution(BaseVoucher.WARNING);
  }

  /**
   * Sets the raw voucher that is wrapped by this TransportVoucher.
   */
  setVoucher(voucher?: BaseVoucher | null) {
    //
    // Bail if this voucher has already been set, the voucher is null,
    // or this TransportVoucher has already been cancelled or resolved.
    //
    if (!voucher || this._voucher || this._cancelled || this._resolved) {
      return this;
    }
    this._voucher = voucher;
    //
    // If the voucher is already complete, then schedule resolution
    // of the voucher on the next tick.  Otherwise listen for
    // VoucherEvents on the voucher.
    //
    if (voucher.isCompleted()) {
      this._scheduleDeferredResolution(voucher.getType());
    } else {
      this._voucherEventHandle = voucher.attachEvent(
        EventDispatcher.ALL,
        this._onVoucherEvent,
        this
      );
    }
    return this;
  }

  /**
   * Creates and returns a chained TransportVoucher.  The specified
   * callback function is called when this voucher is fulfilled, and it
   * should return a new raw voucher to be used in the chained
   * TransportVoucher.
   * @param {VoucherCallback} thenCallback a handler function that takes one
   * parameter, a TransportVoucher, and is called when this TransportVoucher
   * is fulfilled.  It should return a raw voucher that is set on the
   * chained TransportVoucher.
   * @returns {sas.vaviewer.transport.TransportVoucher} the chained
   * TransportVoucher
   */
  then(thenCallback: (voucher: TransportVoucher) => void | BaseVoucher | null) {
    //
    // Create a new TransportVoucher that is chained to this one.
    //
    const chainedTransportVoucher = new TransportVoucher();
    chainedTransportVoucher._chainParent = this;
    this.always(function (thisTransportVoucher: TransportVoucher) {
      //
      // Handle a cancelled parent voucher in a voucher chain.
      // Usually, a cancel happens from the end of the chain,
      // and in that case all child vouchers have been cancelled
      // by the time this always handler is reached, and another call
      // to cancel is just a noop. But there could be a case where
      // cancel is called directly on a TransportVoucher that is
      // somewhere in the middle of a chain. In that case cancel
      // must be called on the children, and propagated downstream.
      //
      if (thisTransportVoucher.isCancelled()) {
        chainedTransportVoucher.cancel();
        return;
      }
      //
      // If this voucher was fulfilled then we execute
      // the thenCallback function.  That function should return
      // a Voucher, which becomes the chainVoucher.
      //
      // If the voucher was cancelled or is in warning then
      // the chainVoucher remains this voucher, which will
      // propagate the cancelled/warning down the chain.
      //
      let chainVoucher = thisTransportVoucher._voucher;
      if (chainVoucher && chainVoucher.getType() === BaseVoucher.FULFILLED) {
        const nextVoucher = thenCallback(thisTransportVoucher);
        if (nextVoucher instanceof BaseVoucher) {
          chainVoucher = nextVoucher;
        }
      }
      //
      // Set the chainVoucher on the chainedTransportVoucher.
      // This will cause the chainedTransportVoucher to be resolved
      // once the voucher resolves.
      //
      if (chainVoucher) {
        chainedTransportVoucher.setVoucher(chainVoucher);
      }
    });

    return chainedTransportVoucher;
  }

  /**
   * Adds a handler callback to be called when this TransportVoucher
   * is fulfilled.
   */
  fulfilled(fulfilledCallback: (voucher: TransportVoucher) => void | BaseVoucher | null) {
    if (fulfilledCallback && this._fulfilledCallbacks) {
      this._fulfilledCallbacks.push(fulfilledCallback);
    }
    return this;
  }

  /**
   * Adds a handler callback to be called when this TransportVoucher
   * is cancelled.
   */
  cancelled(cancelledCallback: (voucher: TransportVoucher) => void | BaseVoucher | null) {
    if (cancelledCallback && this._cancelledCallbacks) {
      this._cancelledCallbacks.push(cancelledCallback);
    }
    return this;
  }

  /**
   * Adds a handler callback to be called when this TransportVoucher
   * is completed with a warning.
   */
  warning(warningCallback: (voucher: TransportVoucher) => void | BaseVoucher | null) {
    if (warningCallback && this._warningCallbacks) {
      this._warningCallbacks.push(warningCallback);
    }
    return this;
  }

  /**
   * Adds a handler callback to be called when this TransportVoucher
   * is completed, regardless of the status. Always callbacks are
   * executed after all fulfilled/cancelled/warning callbacks have
   * been executed.
   */
  always(alwaysCallback: (voucher: TransportVoucher) => void | BaseVoucher | null) {
    if (alwaysCallback && this._alwaysCallbacks) {
      this._alwaysCallbacks.push(alwaysCallback);
    }
    return this;
  }

  /**
   * Cancels this TransportVoucher, causing the raw voucher to be
   * cancelled and the cancel handlers to be called.
   */
  cancel() {
    if (!this._cancelled && !this._resolved) {
      this._cancelled = true;
      //
      // If this TransportVoucher is part of a chain
      // then the parent TransportVoucher needs to be cancelled.
      //
      if (this._chainParent) {
        this._chainParent.cancel();
      }
      //
      // Remove listeners for voucher and download events.
      //
      if (this._voucherEventHandle) {
        this._voucherEventHandle.detach();
        this._voucherEventHandle = null;
      }
      if (this._downloadVoucherEventHandle) {
        this._downloadVoucherEventHandle.detach();
        this._downloadVoucherEventHandle = null;
      }
      //
      // Cancel any existing deferred resolution timer
      //
      this._cancelDeferredResolution();
      //
      // Cancel the voucher
      //
      if (this._voucher) {
        this._voucher.cancel();
      }
      //
      // Schedule the resolution of the cancel
      //
      this._scheduleDeferredResolution(BaseVoucher.CANCELLED);
    }
  }

  /**
   * Returns true if the voucher has been cancelled.
   * @returns true if cancelled
   */
  isCancelled() {
    return this._cancelled;
  }

  /**
   * Gets the warning for this TransportVoucher
   */
  getWarning() {
    return this._voucher ? this._voucher.getBaseWarning() : this._warning;
  }

  /**
   * Gets the transport resource object that is returned by the raw
   * voucher.
   * @returns the resource returned by the voucher
   */
  getRequestedResource() {
    return this._voucher ? this._voucher.getAssociatedModel() : null;
  }

  private _getReportDownloadStatus() {
    const report = this.getRequestedResource();
    if (report instanceof Report) {
      return report.getDownloadStatus();
    }
    return null;
  }

  private _onVoucherEvent(e: PortableEvent) {
    this._resolveVoucher(e.getType());
  }

  /**
   * Schedules a _resolveVoucher call on the next tick.
   * @param {number} voucherType the voucher EventType
   * @private
   */
  private _scheduleDeferredResolution(voucherType: number) {
    if (!this._deferredResolutionTimerId) {
      this._deferredResolutionTimerId = window.setTimeout(
        this._resolveVoucher.bind(this, voucherType),
        0
      );
    }
  }

  /**
   * Cancels the existing scheduled deferred call to _resolveVoucher.
   */
  private _cancelDeferredResolution() {
    if (this._deferredResolutionTimerId) {
      clearTimeout(this._deferredResolutionTimerId);
      this._deferredResolutionTimerId = 0;
    }
  }

  /**
   * Resolves the voucher based on the completed voucher eventType.
   * @param {number} voucherType the EventType constant that is the
   * voucher type (BaseVoucher.getType()).
   * @private
   */
  private _resolveVoucher(voucherType: number) {
    switch (voucherType) {
      case BaseVoucher.FULFILLED:
        //
        // If there are not pending downloads that we are waiting
        // on then resolve the fulfilled callbacks.
        //
        if (!this._handlePendingDownloads(this._getReportDownloadStatus())) {
          this._resolveCallbacks(this._fulfilledCallbacks);
        }
        break;
      case BaseVoucher.WARNING:
        this._resolveCallbacks(this._warningCallbacks);
        break;
      case BaseVoucher.CANCELLED:
        //
        // It is possible the raw voucher to get cancelled without
        // going through the TransportVoucher.cancel() function.
        // That makes it necessary to set the _cancelled flag here.
        //
        this._cancelled = true;
        this._resolveCallbacks(this._cancelledCallbacks);
        break;
    }
  }

  /**
   * Returns the pending download status and adds listeners to the
   * DownloadStatus if needed.
   * @param downloadStatus the report DownloadStatus
   * @returns true if there are pending downloads
   */
  private _handlePendingDownloads(downloadStatus: DownloadStatus | null) {
    let downloadsPending = false;
    if (!downloadStatus) {
      return false;
    }
    if (downloadStatus.getNumberOfPendingDownloads() > 0) {
      //
      // Listen for voucher events on the downloadStatus
      //
      if (!this._downloadVoucherEventHandle) {
        this._downloadVoucherEventHandle = downloadStatus.attachEvent(
          EventDispatcher.ALL,
          this._onVoucherEvent,
          this
        );
      }
      downloadsPending = true;
    }
    return downloadsPending;
  }

  /**
   * Resolves the voucher by executing the specified list of callbacks,
   * executing the always callbacks, then destroying this TransportVoucher.
   */
  _resolveCallbacks(callbacks: VoucherCallback[]) {
    _executeCallbacks(callbacks, this);
    _executeCallbacks(this._alwaysCallbacks, this);
    this._destroy();
    this._resolved = true;
  }

  // @ltjsDestructor
  private _destroy() {
    this._chainParent = null;
    this._fulfilledCallbacks.length = 0;
    this._cancelledCallbacks.length = 0;
    this._warningCallbacks.length = 0;
    this._alwaysCallbacks.length = 0;
  }
}
